import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calculator, DollarSign, Users, TrendingUp, Shield, ArrowRight } from 'lucide-react';

export const InsuranceCalculatorTool: React.FC = () => {
  const [formData, setFormData] = useState({
    annualIncome: '',
    totalDebt: '',
    dependents: '',
    yearsToReplace: '',
    existingSavings: '',
    spouse: 'no',
    mortgageBalance: '',
    education: ''
  });

  const [recommendation, setRecommendation] = useState<{
    recommended: number;
    breakdown: Array<{label: string; amount: number}>;
  } | null>(null);

  const calculateCoverage = () => {
    const income = parseInt(formData.annualIncome) || 0;
    const debt = parseInt(formData.totalDebt) || 0;
    const years = parseInt(formData.yearsToReplace) || 10;
    const savings = parseInt(formData.existingSavings) || 0;
    const mortgage = parseInt(formData.mortgageBalance) || 0;
    const education = parseInt(formData.education) || 0;
    const dependents = parseInt(formData.dependents) || 0;

    // Basic calculation: (Income × Years) + Debts + Mortgage + Education - Savings
    let incomeReplacement = income * years;
    let totalDebts = debt + mortgage;
    let futureNeeds = education + (dependents * 25000); // $25k per dependent for future needs
    
    let totalNeeded = incomeReplacement + totalDebts + futureNeeds - savings;
    
    // Round to nearest $50k
    totalNeeded = Math.ceil(totalNeeded / 50000) * 50000;
    
    // Minimum of $100k, maximum of $5M for reasonableness
    totalNeeded = Math.max(100000, Math.min(5000000, totalNeeded));

    const breakdown = [
      { label: 'Income Replacement', amount: incomeReplacement },
      { label: 'Debt Coverage', amount: totalDebts },
      { label: 'Future Needs', amount: futureNeeds },
      { label: 'Less: Existing Savings', amount: -savings }
    ];

    setRecommendation({
      recommended: totalNeeded,
      breakdown: breakdown.filter(item => item.amount !== 0)
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div className="text-center">
        <Calculator className="h-12 w-12 text-primary mx-auto mb-4" />
        <h2 className="text-3xl font-bold text-foreground mb-4">
          Life Insurance Calculator
        </h2>
        <p className="text-muted-foreground text-lg">
          Calculate how much life insurance coverage you need based on your financial situation
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Calculator Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <DollarSign className="h-5 w-5 mr-2" />
              Your Financial Information
            </CardTitle>
            <CardDescription>
              Enter your current financial details to get a personalized recommendation
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="income">Annual Income</Label>
              <Input
                id="income"
                type="number"
                placeholder="75000"
                value={formData.annualIncome}
                onChange={(e) => setFormData({...formData, annualIncome: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="debt">Total Debt (excluding mortgage)</Label>
              <Input
                id="debt"
                type="number"
                placeholder="25000"
                value={formData.totalDebt}
                onChange={(e) => setFormData({...formData, totalDebt: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="mortgage">Mortgage Balance</Label>
              <Input
                id="mortgage"
                type="number"
                placeholder="200000"
                value={formData.mortgageBalance}
                onChange={(e) => setFormData({...formData, mortgageBalance: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="dependents">Number of Dependents</Label>
              <Select value={formData.dependents} onValueChange={(value) => setFormData({...formData, dependents: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select number of dependents" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0</SelectItem>
                  <SelectItem value="1">1</SelectItem>
                  <SelectItem value="2">2</SelectItem>
                  <SelectItem value="3">3</SelectItem>
                  <SelectItem value="4">4+</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="years">Years to Replace Income</Label>
              <Select value={formData.yearsToReplace} onValueChange={(value) => setFormData({...formData, yearsToReplace: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="How many years?" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5 years</SelectItem>
                  <SelectItem value="10">10 years</SelectItem>
                  <SelectItem value="15">15 years</SelectItem>
                  <SelectItem value="20">20 years</SelectItem>
                  <SelectItem value="25">25 years</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="savings">Existing Savings & Investments</Label>
              <Input
                id="savings"
                type="number"
                placeholder="50000"
                value={formData.existingSavings}
                onChange={(e) => setFormData({...formData, existingSavings: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="education">Future Education Costs</Label>
              <Input
                id="education"
                type="number"
                placeholder="100000"
                value={formData.education}
                onChange={(e) => setFormData({...formData, education: e.target.value})}
              />
            </div>

            <Button 
              onClick={calculateCoverage} 
              className="w-full" 
              size="lg"
              disabled={!formData.annualIncome}
            >
              Calculate My Coverage
              <Calculator className="ml-2 h-4 w-4" />
            </Button>
          </CardContent>
        </Card>

        {/* Results */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Shield className="h-5 w-5 mr-2" />
              Your Recommendation
            </CardTitle>
            <CardDescription>
              Based on your financial information
            </CardDescription>
          </CardHeader>
          <CardContent>
            {recommendation ? (
              <div className="space-y-6">
                <div className="text-center p-6 bg-primary/5 rounded-2xl border border-primary/20">
                  <div className="text-3xl font-bold text-primary mb-2">
                    {formatCurrency(recommendation.recommended)}
                  </div>
                  <div className="text-muted-foreground">
                    Recommended Life Insurance Coverage
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="font-semibold text-foreground">Coverage Breakdown:</h4>
                  {recommendation.breakdown.map((item, index) => (
                    <div key={index} className="flex justify-between items-center py-2 border-b border-border last:border-0">
                      <span className="text-muted-foreground">{item.label}</span>
                      <span className={`font-medium ${item.amount < 0 ? 'text-red-600' : 'text-foreground'}`}>
                        {formatCurrency(Math.abs(item.amount))}
                        {item.amount < 0 && ' (deducted)'}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="pt-4">
                  <Button className="w-full" size="lg" variant="hero">
                    Now Get Your Quote
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  <p className="text-xs text-muted-foreground text-center mt-2">
                    Get personalized quotes for {formatCurrency(recommendation.recommended)} coverage
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <TrendingUp className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  Fill out the form to see your personalized coverage recommendation
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tips */}
      <Card>
        <CardHeader>
          <CardTitle>Coverage Tips</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div className="space-y-2">
              <h4 className="font-semibold text-foreground">Income Replacement</h4>
              <p className="text-muted-foreground">
                Typically 8-12 times your annual income to maintain your family's lifestyle
              </p>
            </div>
            <div className="space-y-2">
              <h4 className="font-semibold text-foreground">Debt Coverage</h4>
              <p className="text-muted-foreground">
                Include mortgage, credit cards, student loans, and other debts
              </p>
            </div>
            <div className="space-y-2">
              <h4 className="font-semibold text-foreground">Future Needs</h4>
              <p className="text-muted-foreground">
                Consider education costs, emergency funds, and final expenses
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};